import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useWoody } from '../../woody/woody.js';
import { useOptimisticallyUpdateInfiniteQueriesWithPredicate, useOptimisticallyUpdateQueriesWithPredicate } from '@fabric-app/queries/utilities';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { toast } from '../../store/alerts.js';
import { userPinnedResourcesQueryKeys } from '../queries/userPinnedResourcesQueryKeys.js';
import { resourceQueryPredicates } from '../../resources/queries/resourceQueryPredicates.js';
import { rootQueryPredicates } from '../../resource-roots/queries/rootQueryPredicates.js';

const useMutationResourceUserPin = () => {
  const { client } = useWoody();
  const queryClient = useQueryClient();
  const optimisticallyUpdateInfiniteQueriesWithPredicate = useOptimisticallyUpdateInfiniteQueriesWithPredicate();
  const optimisticallyUpdateQueriesWithPredicate = useOptimisticallyUpdateQueriesWithPredicate();
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: (variables) => {
      if (variables.action === "pin") {
        return client.v2("/v2/resource/user-pin", {
          method: "post",
          body: variables.data
        });
      } else {
        return client.v2("/v2/resource/user-pin/delete", {
          method: "post",
          body: variables.data
        });
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: userPinnedResourcesQueryKeys.userPins(variables.data.board)
      });
      toast({
        id: "resource-pin",
        replace: true,
        content: variables.action === "pin" ? "Pinned to homescreen" : "Unpinned from homescreen"
      });
      track(variables.action === "pin" ? AnalyticsEvents.PinResources : AnalyticsEvents.UnpinResources, {
        resourceIds: variables.data.resourceIds,
        board: variables.data.board
      });
      try {
        optimisticallyUpdateInfiniteQueriesWithPredicate({
          predicate: (q) => resourceQueryPredicates.resourceListAny(q),
          options: {
            type: "active"
          },
          pageUpdater: (page) => {
            return {
              ...page,
              resources: page.resources.map((resource) => {
                if (variables.data.resourceIds.includes(resource.id)) {
                  return {
                    ...resource,
                    pins: variables.action === "pin" ? [
                      ...resource.pins,
                      variables.data.board
                    ] : resource.pins.filter((pin) => pin !== variables.data.board)
                  };
                }
                return resource;
              })
            };
          }
        });
        optimisticallyUpdateInfiniteQueriesWithPredicate({
          predicate: (q) => rootQueryPredicates.resourceRootAll(q),
          pageUpdater: (page) => {
            if (!page) return page;
            return {
              ...page,
              roots: page.roots.map((root) => {
                if (variables.data.resourceIds.includes(root.folder.id)) {
                  return {
                    ...root,
                    folder: {
                      ...root.folder,
                      pins: [
                        ...root.folder.pins || [],
                        variables.data.board
                      ]
                    }
                  };
                }
                return root;
              })
            };
          }
        });
        variables.data.resourceIds.forEach((resourceId) => {
          optimisticallyUpdateQueriesWithPredicate((q) => resourceQueryPredicates.resourceDetail(q, resourceId), (resourceDetail) => {
            if (!resourceDetail) return resourceDetail;
            return {
              ...resourceDetail,
              pins: variables.action === "pin" ? [
                ...resourceDetail.pins,
                variables.data.board
              ] : resourceDetail.pins.filter((pin) => pin !== variables.data.board)
            };
          });
        });
      } catch (e) {
        console.error(e);
      }
    }
  });
};
const useResourceUserPinToHomescreen = (resource) => {
  const { mutate } = useMutationResourceUserPin();
  const isPinned = resource.pins.includes("user:home");
  const resourceId = resource.id;
  const togglePin = useCallback(() => {
    if (isPinned) {
      mutate({
        data: {
          resourceIds: [
            resourceId
          ],
          board: "user:home"
        },
        action: "unpin"
      });
    } else {
      mutate({
        data: {
          resourceIds: [
            resourceId
          ],
          board: "user:home"
        },
        action: "pin"
      });
    }
  }, [
    isPinned,
    resourceId,
    mutate
  ]);
  return {
    togglePin,
    pinLabel: isPinned ? "Unpin from Home" : "Pin to Home"
  };
};

export { useResourceUserPinToHomescreen };
//# sourceMappingURL=useMutationResourceUserPin.js.map
